/**
 * @fileoverview ホームページ
 * @description P1デザイン対応のランディングページ（PC/モバイル対応）
 */
import { createFileRoute, Link } from '@tanstack/react-router'
import { Bird, LogIn, MessageCircle, Info } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useChat } from '@/hooks/use-chat'
import { TemplateGrid } from '@/components/chat/template-card'
import { TemplateGridSkeleton } from '@/components/chat/loading-skeleton'

/**
 * ホームページコンポーネント
 * @description PC版とモバイル版でレスポンシブに切り替え
 */
function HomePage() {
  const { templates, isLoadingTemplates } = useChat()

  return (
    <div className="flex min-h-screen flex-col bg-[#F5F4F1]">
      {/* PC版レイアウト */}
      <div className="hidden lg:flex lg:min-h-screen">
        {/* 左パネル */}
        <div className="flex flex-1 flex-col gap-10 p-12 xl:p-16">
          {/* ヘッダー */}
          <header className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <span className="text-2xl font-bold tracking-tight text-[#1A1918]">家老空兵衛</span>
              <span className="text-sm font-medium text-[#6D6C6A]">ドローン法規ナビ</span>
            </div>
            <Button asChild className="gap-2 rounded-full bg-[#3D8A5A] px-4 hover:bg-[#2E7D32]">
              <a href="/login">
                <LogIn className="h-4 w-4" />
                ログイン
              </a>
            </Button>
          </header>

          {/* ヒーローセクション（P1デザイン準拠） */}
          <section className="flex flex-col gap-6">
            <h1 className="text-4xl font-bold leading-tight tracking-tight text-[#1A1918] xl:text-5xl">
              ドローン飛行の法規を
              <br />
              大空藩がご案内
            </h1>
            <p className="text-base leading-relaxed text-[#6D6C6A] xl:text-lg">
              家老・空兵衛と若侍・誠之介が、免許、機体登録、飛行エリアの制限など、
              <br />
              ドローン飛行に関する法規や手続きについてお答えします。
            </p>
          </section>

          {/* テンプレートセクション */}
          <section className="flex flex-col gap-5">
            <h2 className="text-base font-semibold text-[#1A1918]">殿からよくいただくご質問</h2>
            {isLoadingTemplates ? (
              <TemplateGridSkeleton count={6} />
            ) : (
              <TemplateGrid
                templates={templates.slice(0, 6)}
                onSelect={(template) => {
                  window.location.href = `/chat?template=${template.id}`
                }}
              />
            )}
          </section>
        </div>

        {/* 右パネル（チャットプレビュー） */}
        <div className="flex w-[480px] flex-col border-l border-[#E5E4E1] bg-white">
          {/* チャットヘッダー */}
          <div className="flex items-center gap-3 border-b border-[#E5E4E1] px-6 py-5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#C8E6C9]">
              <Bird className="h-[18px] w-[18px] text-[#2E7D32]" />
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-base font-semibold text-[#1A1918]">大空藩 飛行御用</span>
              <span className="text-[13px] text-[#6D6C6A]">家老・若侍がお仕えします</span>
            </div>
          </div>

          {/* チャットエリア（プレビュー） */}
          <div className="flex flex-1 flex-col items-center justify-center gap-5 p-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#E8F5E9]">
              <MessageCircle className="h-7 w-7 text-[#3D8A5A]" />
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-[#1A1918]">何でもお気軽にご質問ください</p>
              <p className="mt-2 text-sm leading-relaxed text-[#6D6C6A]">
                左側のテンプレートを選択するか、
                <br />
                下の入力欄から直接質問できます
              </p>
            </div>
          </div>

          {/* 入力エリア */}
          <div className="flex flex-col gap-3 border-t border-[#E5E4E1] p-5">
            <div className="flex items-center justify-center gap-1.5 text-[11px] text-[#9C9B99]">
              <Info className="h-3.5 w-3.5" />
              <span>AIの回答は参考情報です。最終判断はご自身で行ってください</span>
            </div>
            <Link
              to="/chat"
              search={{ template: undefined }}
              className="flex items-center justify-between rounded-3xl bg-[#F5F4F1] px-5 py-3.5"
            >
              <span className="text-[15px] text-[#9C9B99]">
                ドローン飛行について質問してください...
              </span>
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#3D8A5A]">
                <MessageCircle className="h-5 w-5 text-white" />
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* モバイル版レイアウト */}
      <div className="flex flex-col lg:hidden">
        {/* ヘッダー */}
        <header className="flex items-center justify-between px-6 py-5">
          <div className="flex flex-col gap-1">
            <span className="text-2xl font-bold tracking-tight text-[#1A1918]">家老空兵衛</span>
            <span className="text-sm font-medium text-[#6D6C6A]">ドローン法規ナビ</span>
          </div>
          <Button
            asChild
            size="sm"
            className="gap-1.5 rounded-full bg-[#3D8A5A] px-4 hover:bg-[#2E7D32]"
          >
            <a href="/login">
              <LogIn className="h-4 w-4" />
              ログイン
            </a>
          </Button>
        </header>

        {/* メインコンテンツ */}
        <main className="flex flex-1 flex-col gap-8 px-6 py-5">
          {/* 利用制限バッジ */}
          <div className="flex items-center gap-2.5 rounded-xl border border-[#FDBA74] bg-[#FFF7ED] px-4 py-3">
            <Info className="h-[18px] w-[18px] text-[#EA580C]" />
            <span className="text-[13px] font-medium text-[#9A3412]">
              本日の残り: 5回（ログインで100回/日）
            </span>
          </div>

          {/* ヒーローセクション（P1デザイン準拠：説明文なし、ボタンのみ） */}
          <section className="flex flex-col gap-5 rounded-[20px] bg-[#3D8A5A] p-6">
            <h1 className="text-2xl font-bold leading-[1.3] tracking-tight text-white">
              ドローン飛行の法規を
              <br />
              大空藩がご案内
            </h1>
            <Button
              asChild
              className="w-fit gap-2.5 rounded-xl bg-white px-6 py-3.5 text-[15px] font-semibold text-[#3D8A5A] hover:bg-white/90"
            >
              <Link to="/chat" search={{ template: undefined }}>
                <Bird className="h-[18px] w-[18px]" />
                家老に質問する
              </Link>
            </Button>
          </section>
        </main>
      </div>
    </div>
  )
}

/**
 * インデックスルート定義
 */
export const Route = createFileRoute('/')({
  component: HomePage,
})
